/**
 * Shared formatting utilities for dashboard metrics
 *
 * Provides consistent number, duration, and percentage formatting
 * across all dashboard components.
 */

import type { DataPoint, TimeRange } from '../types/dashboard';

/**
 * Period in seconds for each time range (must match backend TIME_RANGES)
 */
const TIME_RANGE_PERIODS: Record<TimeRange, { hours: number; period: number }> =
  {
    '1d': { hours: 24, period: 3600 }, // 1-hour buckets
    '3d': { hours: 72, period: 10800 }, // 3-hour buckets
    '7d': { hours: 168, period: 21600 }, // 6-hour buckets
    '30d': { hours: 720, period: 86400 }, // 24-hour buckets
  };

/**
 * Fill gaps in time series data with zero values.
 *
 * CloudWatch only returns data points for periods with activity.
 * This function generates the complete set of time buckets and
 * fills missing ones with zero values for proper chart rendering.
 *
 * @param data - Sparse data points from CloudWatch
 * @param timeRange - The time range (24h, 7d, 30d)
 * @returns Complete time series with zero-filled gaps
 */
export function fillTimeSeriesGaps(
  data: DataPoint[] | undefined,
  timeRange: TimeRange,
): DataPoint[] {
  if (!data) return [];

  const { hours, period } = TIME_RANGE_PERIODS[timeRange];
  const periodMs = period * 1000;

  // Calculate time range boundaries, aligned to period
  const now = Date.now();
  const endTime = Math.floor(now / periodMs) * periodMs;
  const startTime = endTime - hours * 60 * 60 * 1000;

  // Build a map of existing data points by their aligned timestamp
  const dataMap = new Map<number, number>();
  for (const point of data) {
    const ts = new Date(point.timestamp).getTime();
    // Align to period boundary
    const alignedTs = Math.floor(ts / periodMs) * periodMs;
    dataMap.set(alignedTs, point.value);
  }

  // Generate complete time series with zeros for missing periods
  const result: DataPoint[] = [];
  for (let ts = startTime; ts <= endTime; ts += periodMs) {
    result.push({
      timestamp: new Date(ts).toISOString(),
      value: dataMap.get(ts) ?? 0,
    });
  }

  return result;
}

/**
 * Format a number with locale-specific thousands separators.
 * Returns '-' for undefined values.
 */
export function formatNumber(num: number | undefined): string {
  if (num === undefined) return '-';
  return num.toLocaleString();
}

/**
 * Format a latency value in milliseconds.
 * Returns '-' for undefined values.
 */
export function formatLatency(ms: number | undefined): string {
  if (ms === undefined) return '-';
  return `${Math.round(ms)} ms`;
}

/**
 * Format a duration in seconds with adaptive units (s/m/h).
 * Returns '-' for undefined values.
 */
export function formatDuration(seconds: number | undefined): string {
  if (seconds === undefined) return '-';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

/**
 * Format a percentage value with one decimal place.
 * Returns '-' for undefined values.
 */
export function formatPercentage(pct: number | undefined): string {
  if (pct === undefined) return '-';
  return `${pct.toFixed(1)}%`;
}

/**
 * Format a rate value (per second).
 * Returns '-' for undefined values.
 */
export function formatRate(rate: number | undefined): string {
  if (rate === undefined) return '-';
  return `${rate.toFixed(1)}/s`;
}

/**
 * Format a decimal number with one decimal place.
 * Useful for averages and ratios.
 * Returns '-' for undefined values.
 */
export function formatDecimal(num: number | undefined): string {
  if (num === undefined) return '-';
  return num.toFixed(1);
}

/**
 * Format a timestamp for chart axis display.
 * Uses compact format to minimize X-axis label width.
 * Always shows date for consistency: "1/20 8AM" or "1/20 8:30AM"
 */
export function formatChartTime(timestamp: string): string {
  const date = new Date(timestamp);

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;

  // Only show minutes if not on the hour
  const timeStr =
    minutes === 0
      ? `${hour12}${ampm}`
      : `${hour12}:${String(minutes).padStart(2, '0')}${ampm}`;

  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${month}/${day} ${timeStr}`;
}

/**
 * Format a timestamp with explicit date context.
 * Always shows both date and time for clarity.
 */
export function formatChartTimeWithDate(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a byte value with adaptive units (B/KB/MB/GB).
 * Returns '-' for undefined values.
 */
export function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined) return '-';
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const unitIndex = Math.min(i, units.length - 1);

  return `${(bytes / Math.pow(k, unitIndex)).toFixed(1)} ${units[unitIndex]}`;
}

/**
 * Format large numbers with compact notation (K, M, B).
 * Used for chart displays where space is limited.
 * Returns '-' for undefined values.
 *
 * @example
 * formatCompactNumber(1234) // "1.2K"
 * formatCompactNumber(1234567) // "1.2M"
 * formatCompactNumber(1234567890) // "1.2B"
 */
export function formatCompactNumber(value: number | undefined): string {
  if (value === undefined) return '-';
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toLocaleString();
}

/**
 * Format latency value with adaptive units (ms/s).
 * Used for chart displays where both ms and seconds may be shown.
 *
 * @example
 * formatLatencyCompact(450) // "450ms"
 * formatLatencyCompact(1500) // "1.5s"
 */
export function formatLatencyCompact(ms: number | undefined): string {
  if (ms === undefined) return '-';
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(1)}s`;
  }
  return `${Math.round(ms)}ms`;
}

/**
 * Format a currency value in USD.
 * Uses compact notation for values >= $1000 when compact=true.
 *
 * @param value - The value to format
 * @param compact - Whether to use compact notation for large values
 * @returns Formatted currency string (e.g., "$12.34" or "$1.2K")
 *
 * @example
 * formatCurrency(12.345) // "$12.35"
 * formatCurrency(1234.56, true) // "$1.2K"
 * formatCurrency(undefined) // "-"
 */
export function formatCurrency(
  value: number | undefined,
  compact = false,
): string {
  if (value === undefined) return '-';

  if (compact && value >= 1000) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a date string as a chart-friendly date label.
 * Used for cost trend charts where we show daily data.
 *
 * @example
 * formatChartDate("2026-01-15") // "Jan 15"
 */
export function formatChartDate(date: string): string {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
